import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import Button from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../components/ui/card';
import Input from '../components/ui/input';
import Label from '../components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Loader2 } from 'lucide-react';

const Register = () => {
  const location = useLocation();
  const isGoogleSignup = location.state?.isGoogleSignup;
  const googleUser = location.state?.googleUser;

  // If we have a token but no Google user data, redirect to dashboard
  const navigate = useNavigate();
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (token && !googleUser) {
      navigate('/dashboard');
    }
  }, [navigate, googleUser]);

  // Try to get Google user data from localStorage if not in location state
  useEffect(() => {
    if (isGoogleSignup && !googleUser) {
      const storedGoogleUser = localStorage.getItem('google_user_temp');
      if (storedGoogleUser) {
        const parsedGoogleUser = JSON.parse(storedGoogleUser);
        setFormData(prev => ({
          ...prev,
          name: parsedGoogleUser.name || '',
          email: parsedGoogleUser.email || ''
        }));
      } else {
        // No Google user data, redirect to login
        navigate('/login');
      }
    }
  }, [isGoogleSignup, googleUser, navigate]);

  const [formData, setFormData] = useState({
    name: googleUser?.name || '',
    email: googleUser?.email || '',
    password: '',
    password_confirmation: '',
    organization_name: '',
    organization_size: '',
    website_url: '',
    industry: ''
  });
  const [errors, setErrors] = useState({});

  const { register, loginWithGoogle, completeGoogleRegistration, isLoading } = useAuth();

  const organizationSizes = [
    { value: 'solo', label: '1 person (just me)' },
    { value: 'small', label: '2-10 people' },
    { value: 'medium', label: '11-50 people' },
    { value: 'large', label: '51-200 people' },
    { value: 'enterprise', label: '200+ people' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    // Password validation only for regular signup, not Google signup
    if (!isGoogleSignup) {
      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      }

      if (!formData.password_confirmation) {
        newErrors.password_confirmation = 'Password confirmation is required';
      } else if (formData.password !== formData.password_confirmation) {
        newErrors.password_confirmation = 'Passwords do not match';
      }
    }

    if (!formData.organization_name.trim()) {
      newErrors.organization_name = 'Organization name is required';
    }

    if (!formData.organization_size) {
      newErrors.organization_size = 'Organization size is required';
    }

    if (formData.website_url && !/^https?:\/\/.+/.test(formData.website_url)) {
      newErrors.website_url = 'Website URL must be a valid URL (include http:// or https://)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    let result;
    try {
      if (isGoogleSignup) {
        // Check if we have Google user data
        const storedGoogleUser = localStorage.getItem('google_user_temp');
        if (!storedGoogleUser) {
          toast.error('Google sign-in data not found. Please try signing in with Google again.');
          navigate('/login');
          return;
        }

        // Complete Google registration with organization data
        result = await completeGoogleRegistration({
          organization_name: formData.organization_name,
          organization_size: formData.organization_size,
          website_url: formData.website_url || '',
          industry: formData.industry || ''
        });

        if (!result.success && result.errors) {
          // Handle specific validation errors
          setErrors(result.errors);
          if (result.errors.google_id) {
            toast.error('Google sign-in data is invalid. Please try signing in with Google again.');
            navigate('/login');
            return;
          }
          return;
        }
      } else {
        // Regular registration
        result = await register(formData);
      }

      if (result.success) {
        // Clear Google user data from localStorage
        if (isGoogleSignup) {
          localStorage.removeItem('google_user_temp');
        }
        navigate('/dashboard');
      } else {
        // Handle specific errors
        if (result.errors) {
          setErrors(result.errors);
        }
      }
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.message || 'Registration failed. Please try again.');
    }
  };

  const handleGoogleSignUp = async () => {
    try {
      const result = await loginWithGoogle();
      if (result.success) {
        if (result.requiresOrgSetup) {
          // New user needs to complete organization setup
          navigate('/register', {
            state: {
              googleUser: result.googleUser,
              isGoogleSignup: true
            }
          });
        } else {
          // Existing user, redirect to dashboard
          navigate('/dashboard');
        }
      }
    } catch (error) {
      console.error('Google sign-up failed:', error);
    }
  };

  return (
    <div className="container mx-auto px-4 py-12 flex items-center justify-center min-h-screen">
      <Card className="w-full max-w-2xl">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">
            {isGoogleSignup ? 'Complete Your Organization Setup' : 'Create Account'}
          </CardTitle>
          {isGoogleSignup && (
            <CardDescription>
              Welcome! Please complete your organization information to finish setting up your account.
            </CardDescription>
          )}
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  disabled={isLoading || isGoogleSignup}
                  error={!!errors.name}
                />
                {errors.name && (
                  <p className="text-sm text-destructive">{errors.name}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                  disabled={isLoading || isGoogleSignup}
                  error={!!errors.email}
                />
                {errors.email && (
                  <p className="text-sm text-destructive">{errors.email}</p>
                )}
              </div>
            </div>

            {!isGoogleSignup && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="password">Password *</Label>
                  <Input
                    id="password"
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Enter your password"
                    disabled={isLoading}
                    error={!!errors.password}
                  />
                  {errors.password && (
                    <p className="text-sm text-destructive">{errors.password}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password_confirmation">Confirm Password *</Label>
                  <Input
                    id="password_confirmation"
                    type="password"
                    name="password_confirmation"
                    value={formData.password_confirmation}
                    onChange={handleChange}
                    placeholder="Confirm your password"
                    disabled={isLoading}
                    error={!!errors.password_confirmation}
                  />
                  {errors.password_confirmation && (
                    <p className="text-sm text-destructive">{errors.password_confirmation}</p>
                  )}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="organization_name">Organization Name *</Label>
                <Input
                  id="organization_name"
                  type="text"
                  name="organization_name"
                  value={formData.organization_name}
                  onChange={handleChange}
                  placeholder="Enter your organization name"
                  disabled={isLoading}
                  error={!!errors.organization_name}
                />
                {errors.organization_name && (
                  <p className="text-sm text-destructive">{errors.organization_name}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="organization_size">Organization Size *</Label>
                <Select
                  value={formData.organization_size}
                  onValueChange={(value) => {
                    setFormData(prev => ({ ...prev, organization_size: value }));
                    // Clear error when a selection is made
                    if (errors.organization_size) {
                      setErrors(prev => ({ ...prev, organization_size: '' }));
                    }
                  }}
                >
                  <SelectTrigger className={errors.organization_size ? 'border-destructive' : ''}>
                    <SelectValue placeholder="Select organization size" />
                  </SelectTrigger>
                  <SelectContent>
                    {organizationSizes.map((size) => (
                      <SelectItem key={size.value} value={size.value}>
                        {size.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.organization_size && (
                  <p className="text-sm text-destructive">{errors.organization_size}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="website_url">Website URL (Optional)</Label>
                <Input
                  id="website_url"
                  type="text"
                  name="website_url"
                  value={formData.website_url}
                  onChange={handleChange}
                  placeholder="https://www.yourcompany.com"
                  disabled={isLoading}
                  error={!!errors.website_url}
                />
                {errors.website_url && (
                  <p className="text-sm text-destructive">{errors.website_url}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="industry">Industry (Optional)</Label>
                <Input
                  id="industry"
                  type="text"
                  name="industry"
                  value={formData.industry}
                  onChange={handleChange}
                  placeholder="Enter your industry"
                  disabled={isLoading}
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {isGoogleSignup ? 'Completing Setup...' : 'Create Account'}
                </div>
              ) : (
                isGoogleSignup ? 'Complete Setup' : 'Create Account'
              )}
            </Button>
          </form>

          {!isGoogleSignup && (
            <>
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-muted-foreground/20" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-background text-muted-foreground">
                    Or continue with
                  </span>
                </div>
              </div>

              <Button
                variant="outline"
                className="w-full"
                onClick={handleGoogleSignUp}
                disabled={isLoading}
              >
                <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Sign up with Google
              </Button>
            </>
          )}
        </CardContent>
        {!isGoogleSignup && (
          <CardFooter className="text-center justify-center">
            <p className="text-sm text-muted-foreground">
              Already have an account?{' '}
              <Link
                to="/login"
                className="text-primary hover:underline"
              >
                Sign In
              </Link>
            </p>
          </CardFooter>
        )}
      </Card>
    </div>
  );
};

export default Register;
